import {
  GRATE_TYPES,
  nextAvailableInletName,
  THROAT_ANGLES,
  type InletDesign,
} from "../model/inp/inlets.ts";
import { useT } from "../i18n/hooks.ts";
import { optionLabelKey } from "../i18n/fieldLabels.ts";
import { NumberInput } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

const INLET_TYPES = ["GRATE", "CURB", "SLOTTED", "DROP_GRATE", "DROP_CURB", "CUSTOM"] as const;

/** Full reset (keeping only `name`) rather than a partial merge — there's no sane way to carry over, say, a grate's own Width into a Custom design's own curve name. */
function defaultForType(type: (typeof INLET_TYPES)[number], name: string): InletDesign {
  switch (type) {
    case "GRATE":
    case "DROP_GRATE":
      return {
        name,
        type,
        length: 2,
        width: 2,
        grateType: "GENERIC",
        openAreaFraction: 0.5,
        splashVelocity: 3,
      };
    case "CURB":
    case "DROP_CURB":
      return { name, type, length: 2, height: 0.5, throatAngle: "VERTICAL" };
    case "SLOTTED":
      return { name, type, length: 2, width: 0.5 };
    case "CUSTOM":
      return { name, type, curveName: "" };
  }
}

/** COMBO designs (two [INLETS] rows sharing one ID, see inlets.ts) aren't a selectable Type here — a design's type fully determines its shape below. */
export function InletsEditor({
  inlets,
  onChange,
  selectedName,
  onSelect,
}: {
  inlets: InletDesign[];
  onChange: (inlets: InletDesign[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteInlet,
    rename: renameSelected,
    update,
  } = useNamedCollection(inlets, onChange, selectedName, onSelect);

  function addInlet() {
    const name = nextAvailableInletName(new Set(inlets.map((i) => i.name)));
    add(defaultForType("GRATE", name));
  }

  function setType(type: (typeof INLET_TYPES)[number]) {
    if (!selected) return;
    update(() => defaultForType(type, selected.name));
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={inlets}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addInlet}
        addLabel={t("projectData.addInlet")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("projectData.noInletSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteInlet(selected.name)}
              deleteLabel={t("projectData.deleteInlet")}
            >
              <label className="field-pill">
                <span className="field-pill-label">{t("projectData.inletType")}</span>
                <select value={selected.type} onChange={(e) => setType(e.target.value as never)}>
                  {INLET_TYPES.map((ty) => {
                    const key = optionLabelKey(ty);
                    return (
                      <option key={ty} value={ty}>
                        {key ? t(key) : ty}
                      </option>
                    );
                  })}
                </select>
              </label>
            </NamedCollectionHeader>
            <div className="named-collection-fields">
              {(selected.type === "GRATE" || selected.type === "DROP_GRATE") && (
                <>
                  <label className="options-field">
                    {t("projectData.inletLength")}
                    <NumberInput
                      value={selected.length}
                      onChange={(v) => update((d) => ({ ...d, length: v }))}
                    />
                  </label>
                  <label className="options-field">
                    {t("projectData.inletWidth")}
                    <NumberInput
                      value={selected.width}
                      onChange={(v) => update((d) => ({ ...d, width: v }))}
                    />
                  </label>
                  <label className="options-field">
                    {t("projectData.inletGrateType")}
                    <select
                      value={selected.grateType}
                      onChange={(e) =>
                        update((d) => ({ ...d, grateType: e.target.value as never }))
                      }
                    >
                      {GRATE_TYPES.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </label>
                  {selected.grateType === "GENERIC" && (
                    <>
                      <label className="options-field">
                        {t("projectData.inletOpenAreaFraction")}
                        <NumberInput
                          value={selected.openAreaFraction}
                          onChange={(v) => update((d) => ({ ...d, openAreaFraction: v }))}
                        />
                      </label>
                      <label className="options-field">
                        {t("projectData.inletSplashVelocity")}
                        <NumberInput
                          value={selected.splashVelocity}
                          onChange={(v) => update((d) => ({ ...d, splashVelocity: v }))}
                        />
                      </label>
                    </>
                  )}
                </>
              )}
              {(selected.type === "CURB" || selected.type === "DROP_CURB") && (
                <>
                  <label className="options-field">
                    {t("projectData.inletLength")}
                    <NumberInput
                      value={selected.length}
                      onChange={(v) => update((d) => ({ ...d, length: v }))}
                    />
                  </label>
                  <label className="options-field">
                    {t("projectData.inletHeight")}
                    <NumberInput
                      value={selected.height}
                      onChange={(v) => update((d) => ({ ...d, height: v }))}
                    />
                  </label>
                  {selected.type === "CURB" && (
                    <label className="options-field">
                      {t("projectData.inletThroatAngle")}
                      <select
                        value={selected.throatAngle}
                        onChange={(e) =>
                          update((d) => ({ ...d, throatAngle: e.target.value as never }))
                        }
                      >
                        {THROAT_ANGLES.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </>
              )}
              {selected.type === "SLOTTED" && (
                <>
                  <label className="options-field">
                    {t("projectData.inletLength")}
                    <NumberInput
                      value={selected.length}
                      onChange={(v) => update((d) => ({ ...d, length: v }))}
                    />
                  </label>
                  <label className="options-field">
                    {t("projectData.inletWidth")}
                    <NumberInput
                      value={selected.width}
                      onChange={(v) => update((d) => ({ ...d, width: v }))}
                    />
                  </label>
                </>
              )}
              {selected.type === "CUSTOM" && (
                <label className="options-field">
                  {t("projectData.inletCurveName")}
                  <input
                    type="text"
                    value={selected.curveName}
                    onChange={(e) => update((d) => ({ ...d, curveName: e.target.value }))}
                  />
                </label>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
